import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
    Checkbox,
    IconButton,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Progress
} from "@material-tailwind/react";
import {
    PlusIcon,
    TrashIcon,
    EllipsisVerticalIcon,
    PencilIcon
} from "@heroicons/react/24/outline";
import ConfirmModal from '../../common/ConfirmModal';

const TaskChecklistSection = ({ taskId }) => {
    const { rootUrl } = require('../../../config/config');
    const [token] = useState(localStorage.getItem('authToken') || '');
    const [userId] = useState(localStorage.getItem('userId') || '');

    const [checklists, setChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewChecklistDialog, setShowNewChecklistDialog] = useState(false);
    const [newChecklistTitle, setNewChecklistTitle] = useState('');
    const [newItemTitle, setNewItemTitle] = useState('');
    const [addingItemToChecklist, setAddingItemToChecklist] = useState(null);
    const [editingChecklist, setEditingChecklist] = useState(null);
    const [editChecklistTitle, setEditChecklistTitle] = useState('');
    const [deleteChecklistId, setDeleteChecklistId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const fetchChecklists = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${rootUrl}/api/tasks/${taskId}/checklists`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                const checklistsData = response.data.data;

                // Fetch items for each checklist
                const checklistsWithItems = await Promise.all(
                    checklistsData.map(async (checklist) => {
                        const itemsResponse = await axios.get(
                            `${rootUrl}/api/tasks/checklists/${checklist.checklist_id}/items`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        );

                        return {
                            ...checklist,
                            items: itemsResponse.data.success ? itemsResponse.data.data : []
                        };
                    })
                );

                setChecklists(checklistsWithItems);
            }
        } catch (error) {
            console.error('Error fetching checklists:', error);
        } finally {
            setLoading(false);
        }
    }, [rootUrl, taskId, token]);

    useEffect(() => {
        fetchChecklists();
    }, [fetchChecklists]);

    const handleCreateChecklist = async () => {
        if (!newChecklistTitle.trim()) return;

        try {
            const response = await axios.post(
                `${rootUrl}/api/tasks/${taskId}/checklists`,
                { title: newChecklistTitle, created_by: userId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setNewChecklistTitle('');
                setShowNewChecklistDialog(false);
                fetchChecklists();
            }
        } catch (error) {
            console.error('Error creating checklist:', error);
        }
    };

    const handleUpdateChecklistTitle = async (checklistId) => {
        if (!editChecklistTitle.trim()) return;

        try {
            const response = await axios.put(
                `${rootUrl}/api/tasks/checklists/${checklistId}`,
                { title: editChecklistTitle, updated_by: userId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setEditingChecklist(null);
                setEditChecklistTitle('');
                fetchChecklists();
            }
        } catch (error) {
            console.error('Error updating checklist:', error);
        }
    };

    const handleDeleteChecklist = async () => {
        if (!deleteChecklistId) return;

        setDeleteLoading(true);
        setDeleteError('');

        try {
            const response = await axios.delete(
                `${rootUrl}/api/tasks/checklists/${deleteChecklistId}?deleted_by=${userId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                fetchChecklists();
                setDeleteChecklistId(null);
            } else {
                setDeleteError(response.data.message || 'Failed to delete checklist');
            }
        } catch (error) {
            console.error('Error deleting checklist:', error);
            setDeleteError(error?.response?.data?.message || 'Failed to delete checklist');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleAddItem = async (checklistId) => {
        if (!newItemTitle.trim()) return;

        try {
            const response = await axios.post(
                `${rootUrl}/api/tasks/checklists/${checklistId}/items`,
                { title: newItemTitle, created_by: userId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setNewItemTitle('');
                setAddingItemToChecklist(null);
                fetchChecklists();
            }
        } catch (error) {
            console.error('Error adding item:', error);
        }
    };

    const handleToggleItem = async (itemId, currentStatus) => {
        try {
            const response = await axios.put(
                `${rootUrl}/api/tasks/checklist-items/${itemId}`,
                {
                    is_completed: !currentStatus,
                    updated_by: userId
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                fetchChecklists();
            }
        } catch (error) {
            console.error('Error toggling item:', error);
        }
    };

    const handleDeleteItem = async (itemId) => {
        try {
            const response = await axios.delete(
                `${rootUrl}/api/tasks/checklist-items/${itemId}?deleted_by=${userId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                fetchChecklists();
            }
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    const calculateProgress = (checklist) => {
        if (!checklist.total_items || checklist.total_items === 0) return 0;
        return Math.round((checklist.completed_items / checklist.total_items) * 100);
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Typography>Loading checklists...</Typography>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex justify-between items-center">
                <Typography variant="h5" color="blue-gray">
                    Checklists
                </Typography>
                <Button
                    size="sm"
                    className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#E04E2E]"
                    onClick={() => setShowNewChecklistDialog(true)}
                >
                    <PlusIcon className="h-4 w-4" />
                    New Checklist
                </Button>
            </div>

            {/* Checklists */}
            {checklists.length === 0 ? (
                <Card className="bg-gray-50">
                    <CardBody className="text-center py-8">
                        <Typography color="gray">
                            No checklists yet. Create one to get started!
                        </Typography>
                    </CardBody>
                </Card>
            ) : (
                <div className="space-y-4">
                    {checklists.map((checklist) => (
                        <Card key={checklist.checklist_id} className="border border-gray-200">
                            <CardBody className="p-4">
                                {/* Checklist Header */}
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex-1">
                                        {editingChecklist === checklist.checklist_id ? (
                                            <div className="flex gap-2">
                                                <Input
                                                    value={editChecklistTitle}
                                                    onChange={(e) => setEditChecklistTitle(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleUpdateChecklistTitle(checklist.checklist_id);
                                                        }
                                                    }}
                                                    className="!border-[#FF5733] focus:!border-[#FF5733]"
                                                />
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleUpdateChecklistTitle(checklist.checklist_id)}
                                                    className="bg-[#FF5733] hover:bg-[#E04E2E]"
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setEditingChecklist(null);
                                                        setEditChecklistTitle('');
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <Typography variant="h6" color="blue-gray">
                                                    {checklist.title}
                                                </Typography>
                                                <Typography variant="small" color="gray">
                                                    ({checklist.completed_items}/{checklist.total_items})
                                                </Typography>
                                            </div>
                                        )}
                                        <Progress
                                            value={calculateProgress(checklist)}
                                            color="orange"
                                            className="mt-2"
                                        />
                                    </div>
                                    <Menu>
                                        <MenuHandler>
                                            <IconButton variant="text" size="sm">
                                                <EllipsisVerticalIcon className="h-5 w-5" />
                                            </IconButton>
                                        </MenuHandler>
                                        <MenuList>
                                            <MenuItem
                                                onClick={() => {
                                                    setEditingChecklist(checklist.checklist_id);
                                                    setEditChecklistTitle(checklist.title);
                                                }}
                                                className="flex items-center gap-2"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                                Edit
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => setDeleteChecklistId(checklist.checklist_id)}
                                                className="flex items-center gap-2 text-red-500"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                                Delete
                                            </MenuItem>
                                        </MenuList>
                                    </Menu>
                                </div>

                                {/* Checklist Items */}
                                <div className="space-y-2 ml-2">
                                    {checklist.items.map((item) => (
                                        <div
                                            key={item.item_id}
                                            className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50"
                                        >
                                            <div className="flex items-center gap-3 flex-1">
                                                <Checkbox
                                                    checked={item.is_completed}
                                                    onChange={() => handleToggleItem(item.item_id, item.is_completed)}
                                                    color="orange"
                                                />
                                                <Typography
                                                    className={`${item.is_completed
                                                            ? 'line-through text-gray-500'
                                                            : 'text-gray-900'
                                                        }`}
                                                >
                                                    {item.title}
                                                </Typography>
                                            </div>
                                            <IconButton
                                                variant="text"
                                                size="sm"
                                                color="red"
                                                onClick={() => handleDeleteItem(item.item_id)}
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </IconButton>
                                        </div>
                                    ))}
                                </div>

                                {/* Add Item */}
                                {addingItemToChecklist === checklist.checklist_id ? (
                                    <div className="mt-3 flex gap-2 ml-2">
                                        <Input
                                            placeholder="Enter item title..."
                                            value={newItemTitle}
                                            onChange={(e) => setNewItemTitle(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleAddItem(checklist.checklist_id);
                                                }
                                            }}
                                            className="!border-[#FF5733] focus:!border-[#FF5733]"
                                        />
                                        <Button
                                            size="sm"
                                            onClick={() => handleAddItem(checklist.checklist_id)}
                                            className="bg-[#FF5733] hover:bg-[#E04E2E]"
                                        >
                                            Add
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            onClick={() => {
                                                setAddingItemToChecklist(null);
                                                setNewItemTitle('');
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                ) : (
                                    <Button
                                        size="sm"
                                        variant="text"
                                        className="mt-2 ml-2 flex items-center gap-2 text-[#FF5733]"
                                        onClick={() => setAddingItemToChecklist(checklist.checklist_id)}
                                    >
                                        <PlusIcon className="h-4 w-4" />
                                        Add Item
                                    </Button>
                                )}
                            </CardBody>
                        </Card>
                    ))}
                </div>
            )}

            {/* New Checklist Dialog */}
            <Dialog
                open={showNewChecklistDialog}
                handler={() => setShowNewChecklistDialog(false)}
                size="sm"
            >
                <DialogHeader>Create New Checklist</DialogHeader>
                <DialogBody>
                    <Input
                        label="Checklist Title"
                        value={newChecklistTitle}
                        onChange={(e) => setNewChecklistTitle(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleCreateChecklist();
                            }
                        }}
                        className="!border-[#FF5733] focus:!border-[#FF5733]"
                    />
                </DialogBody>
            <DialogFooter className="space-x-2">
                <Button
                    variant="outlined"
                        onClick={() => {
                            setShowNewChecklistDialog(false);
                            setNewChecklistTitle('');
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateChecklist}
                        className="bg-[#FF5733] hover:bg-[#E04E2E]"
                    >
                        Create
                    </Button>
                </DialogFooter>
            </Dialog>

            <ConfirmModal
                isOpen={Boolean(deleteChecklistId)}
                title="Delete checklist"
                description="This checklist and its items will be removed from the task."
                onCancel={() => {
                    setDeleteChecklistId(null);
                    setDeleteError('');
                }}
                onConfirm={handleDeleteChecklist}
                confirmText="Delete"
                cancelText="Cancel"
                loading={deleteLoading}
                error={deleteError}
                variant="danger"
            />
        </div>
    );
};

export default TaskChecklistSection;
